import { ArrowLeft, CornerUpLeft } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LandingHeader } from "@/components/landing/LandingHeader";
import { Link } from "@/components/ui/Link";
import { SkyBackdrop } from "@/components/ui/SkyBackdrop";
import { useSiteLocale } from "@/components/site/useSiteLocale";

const copy = {
  en: {
    signal: "Signal lost",
    title: "This creative signal drifted off course.",
    description: "The page may have moved, or this link no longer leads anywhere. Let’s get you back to familiar skies.",
    home: "Return to Zesrin",
    back: "Go back",
  },
  zh: {
    signal: "信号失联",
    title: "这条创意信号，飘出了航线。",
    description: "页面可能已被移动，或这个链接已经失效。回到熟悉的天空，继续寻找下一步。",
    home: "返回 Zesrin",
    back: "返回上一页",
  },
} as const;

export default function NotFoundPage() {
  const { locale, toggleLocale } = useSiteLocale("en");
  const initialDocumentLanguage = useRef(document.documentElement.lang);
  const navigate = useNavigate();
  const text = copy[locale];

  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  }, [locale]);

  useEffect(() => () => {
    document.documentElement.lang = initialDocumentLanguage.current;
  }, []);

  function goBack() {
    const historyIndex = Number(window.history.state?.idx ?? 0);
    if (historyIndex > 0) {
      navigate(-1);
      return;
    }
    navigate("/", { replace: true });
  }

  return (
    <div className="studio-notFound-page" lang={locale === "zh" ? "zh-CN" : "en"}>
      <LandingHeader
        variant="site"
        tone="light"
        locale={locale}
        onLocaleChange={toggleLocale}
      />

      <SkyBackdrop className="studio-notFound-scene" id="top" renderMode="landing" role="main">
        <div className="studio-notFound-shade" aria-hidden="true" />

        <div className="studio-notFound-art" aria-hidden="true">
          <span>404</span>
          <i />
        </div>

        <section className="studio-notFound-content" aria-labelledby="not-found-title">
          <p className="studio-notFound-kicker"><span>404</span>{text.signal}</p>
          <h1 id="not-found-title">{text.title}</h1>
          <p className="studio-notFound-description">{text.description}</p>
          <div className="studio-notFound-actions">
            <Link className="studio-notFound-home" href="/">
              <ArrowLeft aria-hidden="true" />
              {text.home}
            </Link>
            <button className="studio-notFound-back" type="button" onClick={goBack}>
              <CornerUpLeft aria-hidden="true" />
              {text.back}
            </button>
          </div>
        </section>
      </SkyBackdrop>
    </div>
  );
}
